import { useState, FormEvent } from "react";
import { useAdminSession } from "@/auth/AdminSession";
import ApiService from "@/services/ApiService";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Frame,
  Panel,
  Feedback,
  LoadState,
  useData,
  useMutation,
  roles,
  japanDate,
} from "./ui";
type Session = {
  id: number;
  current: boolean;
  created_at: string;
  last_used_at: string;
  expires_at: string;
};
export default function Account() {
  const { session, refresh } = useAdminSession();
  const op = useMutation();
  const sessions = useData<Session[]>("auth/sessions");
  const [profile, setProfile] = useState({
    ...session.user,
    current_password: "",
    password: "",
  });
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [setup, setSetup] = useState<{
    secret: string;
    provisioning_uri: string;
  } | null>(null);
  const [recovery, setRecovery] = useState<string[]>([]);
  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (
      await op.run(async () => {
        await ApiService.put("auth/profile", {
          ...profile,
          password: profile.password || null,
        });
        await refresh();
      })
    )
      setProfile({ ...profile, password: "", current_password: "" });
  };
  return (
    <Frame
      title="アカウントとセキュリティ"
      description={roles[session.platform_role ?? ""] ?? "組織スタッフ"}
    >
      <Feedback error={op.error} notice={op.notice} />
      <Panel title="プロフィール">
        <form onSubmit={save} className="grid sm:grid-cols-2 gap-4">
          <label>
            名前
            <Input
              required
              value={profile.name}
              onChange={(e) => setProfile({ ...profile, name: e.target.value })}
            />
          </label>
          <label>
            メールアドレス
            <Input
              required
              type="email"
              value={profile.email}
              onChange={(e) =>
                setProfile({ ...profile, email: e.target.value })
              }
            />
          </label>
          <label>
            現在のパスワード
            <Input
              required
              type="password"
              autoComplete="current-password"
              value={profile.current_password}
              onChange={(e) =>
                setProfile({ ...profile, current_password: e.target.value })
              }
            />
          </label>
          <label>
            新しいパスワード（変更する場合、12文字以上）
            <Input
              type="password"
              minLength={12}
              autoComplete="new-password"
              value={profile.password}
              onChange={(e) =>
                setProfile({ ...profile, password: e.target.value })
              }
            />
          </label>
          <p className="sm:col-span-2 text-sm text-muted-foreground">
            OOAK共通アカウントを更新します。パスワード変更時は他の管理画面セッションを終了します。
          </p>
          <Button disabled={op.busy}>保存</Button>
        </form>
      </Panel>
      <Panel title="二要素認証">
        <p>{session.mfa_enabled ? "有効" : "無効"}</p>
        <form
          className="space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            if (session.mfa_enabled)
              await op.run(async () => {
                await ApiService.delete("auth/mfa", { password, otp });
                await refresh();
              }, "二要素認証を無効にしました。");
            else if (setup)
              await op.run(async () => {
                const r = await ApiService.post("auth/mfa/confirm", { otp });
                setRecovery(r.data.data.recovery_codes);
                setSetup(null);
                await refresh();
              }, "二要素認証を有効にしました。リカバリーコードを保存してください。");
            else
              await op.run(async () => {
                const r = await ApiService.post("auth/mfa/setup", { password });
                setSetup(r.data.data);
              }, "認証アプリに秘密鍵を登録し、認証コードを入力してください。");
            setPassword("");
            setOtp("");
          }}
        >
          {!setup && (
            <label className="block">
              現在のパスワード
              <Input
                required
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
          )}
          {setup && (
            <div className="space-y-2">
              <p>認証アプリに「OOAK JOB」として登録してください。</p>
              <label className="block">
                秘密鍵
                <Input readOnly value={setup.secret} />
              </label>
              <details>
                <summary>認証アプリ用URI</summary>
                <p className="break-all text-sm">{setup.provisioning_uri}</p>
              </details>
            </div>
          )}
          {(setup || session.mfa_enabled) && (
            <label className="block">
              認証コード / リカバリーコード
              <Input
                required
                autoComplete="one-time-code"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
              />
            </label>
          )}
          <Button
            variant={session.mfa_enabled ? "destructive" : "default"}
            disabled={op.busy}
          >
            {session.mfa_enabled
              ? "二要素認証を無効にする"
              : setup
                ? "認証コードを確認"
                : "二要素認証を設定"}
          </Button>
        </form>
        {!!recovery.length && (
          <div className="border rounded-lg p-4 space-y-3">
            <h3 className="font-semibold">リカバリーコード（一度だけ表示）</h3>
            <p className="text-sm">
              各コードは1回のみ使用できます。安全な場所に保存してください。
            </p>
            <pre className="whitespace-pre-wrap">{recovery.join("\n")}</pre>
            <Button variant="outline" onClick={() => setRecovery([])}>
              保存したので閉じる
            </Button>
          </div>
        )}
      </Panel>
      <Panel title="ログイン中のセッション">
        <LoadState {...sessions} />
        {sessions.data?.map((s) => (
          <div
            key={s.id}
            className="border-b py-3 flex flex-wrap justify-between gap-3"
          >
            <div>
              <p>
                セッション #{s.id} {s.current && "（この端末）"}
              </p>
              <p className="text-sm text-muted-foreground">
                最終使用 {japanDate(s.last_used_at)} / 有効期限{" "}
                {japanDate(s.expires_at)}
              </p>
            </div>
            <Button
              variant="outline"
              disabled={op.busy || s.current}
              onClick={() =>
                op.run(async () => {
                  await ApiService.delete("auth/sessions/" + s.id);
                  sessions.reload();
                }, "セッションを終了しました。")
              }
            >
              終了
            </Button>
          </div>
        ))}
      </Panel>
    </Frame>
  );
}
